/**
 * LTS 자막 번역 타겟 언어 어휘 (v1 = en | ko).
 *
 * BE `CaptionTranslationContract.SUPPORTED_TARGET_LANGUAGES` · ai-api `TARGET_LANGUAGE_LABELS` 와
 * 같은 어휘를 쓴다. 언어를 늘릴 때는 세 곳 + i18n 라벨 키(`caption-translation-language-{code}`)를
 * 함께 늘린다. 콘텐츠 언어(`language`)·UI locale 과는 별개 축이다.
 *
 * 생성 화면(`features/long-to-short`)과 에디터(`features/remotion-editor`) 두 슬라이스가 같이 쓰므로
 * feature 가 아니라 shared 에 둔다(FSD: cross-slice import 금지).
 */
export const CAPTION_TARGET_LANGUAGES = ['en', 'ko'] as const;

export type CaptionTargetLanguage = (typeof CAPTION_TARGET_LANGUAGES)[number];

/** 타겟 언어 기본값 — 스토어 초기값·저장값 복원 실패 시 폴백. */
export const DEFAULT_CAPTION_TARGET_LANGUAGE: CaptionTargetLanguage = 'en';

/** 언어 코드별 i18n 라벨 키. */
export function captionTargetLanguageLabelKey(
  language: CaptionTargetLanguage,
): string {
  return `long-to-short.caption-translation-language-${language}`;
}

export function isCaptionTargetLanguage(
  value: unknown,
): value is CaptionTargetLanguage {
  return (
    typeof value === 'string' &&
    (CAPTION_TARGET_LANGUAGES as readonly string[]).includes(value)
  );
}

/** 생성 화면의 자막 번역 선택값 (localStorage 저장 shape). */
export interface CaptionTranslationSelection {
  enabled: boolean;
  targetLanguage: CaptionTargetLanguage;
}

export const CAPTION_TRANSLATION_STORAGE_KEY = 'lts-caption-translation';

/** 저장된 JSON 문자열을 선택값으로 환원한다. 형식이 깨졌으면 null(= 기본값 사용). */
export function parseStoredCaptionTranslation(
  raw: string | null,
): CaptionTranslationSelection | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { enabled, targetLanguage } = parsed as Record<string, unknown>;
    if (typeof enabled !== 'boolean') return null;
    return {
      enabled,
      targetLanguage: isCaptionTargetLanguage(targetLanguage)
        ? targetLanguage
        : DEFAULT_CAPTION_TARGET_LANGUAGE,
    };
  } catch {
    return null;
  }
}

/** 저장된 선택값 읽기 (SSR·차단 환경에서는 null). */
export function readStoredCaptionTranslation(): CaptionTranslationSelection | null {
  if (typeof window === 'undefined') return null;
  try {
    return parseStoredCaptionTranslation(
      localStorage.getItem(CAPTION_TRANSLATION_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

/** 선택값 저장 (차단 환경에서는 조용히 무시). */
export function writeStoredCaptionTranslation(
  selection: CaptionTranslationSelection,
): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      CAPTION_TRANSLATION_STORAGE_KEY,
      JSON.stringify(selection),
    );
  } catch {
    // ignore
  }
}
